import React, { useMemo } from 'react';
import { CalendarTodayOutlined } from '@mui/icons-material';
import { makeStyles } from '~/UIKit/Style';
import { PRIMARY_BLUE } from '~/Constants/Colors.constants';
import { FONT_SIZE_SMALLISH, FONT_SIZE_MEDIUM } from '~/Constants/UI.constants';
import ClearIcon from '@mui/icons-material/Clear';
import { Button } from '~/UIKit';
import { useDateFns } from '~/Hooks';

export const RangePickerStyles = makeStyles((theme) => ({
  inputWraper: {
    border: `1px solid ${PRIMARY_BLUE}`,
    borderRadius: 3,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: 28,
    margin: '0px 16px',
    width: 240,
    cursor: 'pointer',
    position: 'relative',
    [theme.breakpoints.down('md')]: {
      justifySelf: 'end',
    },
  },
  dateFieldWrapper: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    height: '100%',
    width: '100%',
    color: PRIMARY_BLUE,
    fontFamily: 'fitogram, sans-serif',
    'input:nth-child(2)': {
      borderLeft: '1px solid',
      borderBottomLeftRadius: 0,
      borderTopLeftRadius: 0,
    },
  },
  dateField: {
    fontSize: FONT_SIZE_SMALLISH,
    fontWeight: 'bold',
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    cursor: 'pointer',
    border: 'none',
    borderRadius: 3,
    textAlign: 'center',
    color: 'inherit',
    height: '80%',
    position: 'relative',
    top: '10%',
    '::placeholder ': {
      color: PRIMARY_BLUE,
    },
    '&:focus-visible': {
      outline: 'none',
    },
  },
  iconWrapper: {
    color: PRIMARY_BLUE,
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    paddingRight: 5,
    paddingLeft: 5,
    borderLeft: `1px solid ${PRIMARY_BLUE}`,
    '& .MuiSvgIcon-root': {
      fontSize: FONT_SIZE_MEDIUM,
    },
  },
  clearBtn: {
    width: 28,
    minWidth: 28,
    height: 28,
    padding: 0,
    color: PRIMARY_BLUE,
    outline: 'none',
    border: 'none',
    '&:focus': {
      outline: 'none',
      border: 'none',
    },
  },
  clearIcon: {
    fontSize: FONT_SIZE_SMALLISH,
  },
}));

interface IInputRangePickerProps {
  startProps?: any;
  endProps?: any;
  handleClearDate?: () => void;
  handleOpenDatePicker?: () => void;
  showClear?: boolean;
}

export default function InputRangePicker({
  startProps,
  endProps,
  handleClearDate,
  handleOpenDatePicker = () => {},
  showClear = false,
}: IInputRangePickerProps) {
  const { classes } = RangePickerStyles();
  const { formatDate } = useDateFns();
  const { value: start } = startProps?.inputProps;
  const { value: end } = endProps?.inputProps;
  const dateFormat = 'dd LLL yyyy';
  const isToday = useMemo(
    () => start && end && formatDate(start, dateFormat) === formatDate(end, dateFormat),
    [start, end],
  );
  return (
    <div className={classes.inputWraper}>
      <div className={classes.dateFieldWrapper} data-testid="date-range-input-container">
        <input
          ref={startProps.inputRef as React.Ref<HTMLInputElement>}
          {...startProps.inputProps}
          value={start ? formatDate(start, dateFormat) : ''}
          placeholder=""
          className={classes.dateField}
          data-testid="date-range-input-from"
          onClick={handleOpenDatePicker}
          readOnly
        />
        <input
          ref={endProps.inputRef as React.Ref<HTMLInputElement>}
          {...endProps.inputProps}
          value={end ? formatDate(end, dateFormat) : ''}
          placeholder=""
          className={classes.dateField}
          onClick={handleOpenDatePicker}
          readOnly
        />
      </div>
      {(showClear || (start && end && !isToday)) && (
        <Button
          className={classes.clearBtn}
          onClick={handleClearDate}
          variant="outlined"
          data-testid="date-range-input-clear-button"
        >
          <ClearIcon className={classes.clearIcon} />
        </Button>
      )}
      <div className={classes.iconWrapper} onClick={handleOpenDatePicker}>
        <CalendarTodayOutlined />
      </div>
    </div>
  );
}
